// docs/116 CI7: the inn bard's six tunes. The audio is `music.tavern` (variant = tune id), made by
// tools/tavern-music.py; every number here is that script's (`python3 tools/tavern-music.py --manifest`)
// and `seconds` is measured from the files. Change the tunes there, re-render, and update this table
// (and its Rust mirror in fk-world) together.

export type TuneSeason = 'spring' | 'summer' | 'autumn' | 'winter';

export interface Tune {
  id: number;
  name: string;
  kind: 'jig' | 'reel' | 'drinking song' | 'slow air' | 'hornpipe' | 'carol';
  /** The file's whole length, its last chord's ring included. */
  seconds: number;
  /** Beats a minute: the felt beat (a jig's dotted crotchet, the others' crotchet). */
  bpm: number;
  beatsPerBar: number;
  /** Seconds into the tune the crowd starts clapping (Infinity = never). */
  clapFrom: number;
  /** Seconds into the tune the crowd stops (the final chord's bar). */
  clapTo: number;
  /** The clap pattern within a bar, in beats from the bar's start. */
  claps: number[];
  /** Foot stomps within a bar while the crowd claps (the rowdy tunes), in beats from the bar's start. */
  stomps: number[];
  season?: TuneSeason;
  /** The first downbeat, in seconds (a short quiet lead before the band starts). */
  offset: number;
}

export const TUNES: readonly Tune[] = [
  { id: 0, name: 'The Drowsy Lantern', kind: 'jig', seconds: 55.5, bpm: 120, beatsPerBar: 2, offset: 0.3, clapFrom: 36.3, clapTo: 52.3, claps: [0, 1], stomps: [] },
  { id: 1, name: 'Barley in the Rafters', kind: 'reel', seconds: 63.7, bpm: 200, beatsPerBar: 4, offset: 0.3, clapFrom: 41.1, clapTo: 60.3, claps: [1, 3], stomps: [0, 2] },
  { id: 2, name: 'Fill the Tankard High', kind: 'drinking song', seconds: 63.652, bpm: 126, beatsPerBar: 4, offset: 0.3, clapFrom: 36.490476, clapTo: 59.347619, claps: [1, 3], stomps: [0, 2] },
  { id: 3, name: "The Ferryman's Daughter", kind: 'slow air', seconds: 61.357, bpm: 84, beatsPerBar: 3, offset: 0.3, clapFrom: Infinity, clapTo: Infinity, claps: [], stomps: [] },
  { id: 4, name: 'Hob and the Goose', kind: 'hornpipe', seconds: 62.5, bpm: 144, beatsPerBar: 4, offset: 0.3, clapFrom: 28.633333, clapTo: 58.633333, claps: [1, 3], stomps: [0, 2] },
  { id: 5, name: 'Midwinter Bells', kind: 'carol', seconds: 63.7, bpm: 132, beatsPerBar: 3, offset: 0.3, clapFrom: 24.845455, clapTo: 58.936364, claps: [1, 2], stomps: [], season: 'winter' },
];

/** CI7: between requests the bard plays the slot's tune every SLOT_MS of server time. */
export const SLOT_MS = 240_000;

/** lowbias32 (a 32-bit integer mix); the Rust mirror is the same with u32 wrapping arithmetic. */
function mix32(x: number): number {
  x = (x ^ (x >>> 16)) >>> 0;
  x = Math.imul(x, 0x7feb352d) >>> 0;
  x = (x ^ (x >>> 15)) >>> 0;
  x = Math.imul(x, 0x846ca68b) >>> 0;
  return (x ^ (x >>> 16)) >>> 0;
}

/** Each tune's weight in a slot: a seasonal tune only in its season, and twice as likely then. */
function weight(t: Tune, season: string | undefined): number {
  if (!t.season) return 1;
  return t.season === season ? 2 : 0;
}

/** A weighted draw by `h`, never one of `not`. */
function pick(h: number, season: string | undefined, not: readonly number[] = []): number {
  const w = TUNES.map((t) => (not.includes(t.id) ? 0 : weight(t, season)));
  let r = h % w.reduce((a, b) => a + b, 0);
  for (let i = 0; i < w.length; i++) {
    if (r < w[i]!) return i;
    r -= w[i]!;
  }
  return 0;
}

/** The slot's own draw: `villageSeed` and `slot` taken as unsigned 32-bit (their low 32 bits). */
const draw = (villageSeed: number, slot: number) => mix32((mix32(villageSeed >>> 0) ^ (slot >>> 0)) >>> 0);

/** An even slot's tune: its own draw, or on a feast week the season's tune when it has one. */
function evenTune(villageSeed: number, slot: number, season: string | undefined, feast: boolean): number {
  const own = feast ? TUNES.find((t) => t.season !== undefined && t.season === season) : undefined;
  return own ? own.id : pick(draw(villageSeed, slot), season);
}

/**
 * CI7/CI15: the tune the bard plays in server-time slot `slot` (`floor(server ms / SLOT_MS)`) when nobody
 * has asked for one. Deterministic: every client and the module pick the same. Even slots draw (the
 * winter carol only in winter, twice as likely then; on a feast week every even slot is the season's
 * tune), odd slots draw among the tunes their two even neighbours are not, so no tune plays twice running.
 */
export function scheduledTune(villageSeed: number, slot: number, season?: string, feast = false): number {
  if ((slot & 1) === 0) return evenTune(villageSeed, slot, season, feast);
  const around = [evenTune(villageSeed, slot - 1, season, feast), evenTune(villageSeed, slot + 1, season, feast)];
  return pick(draw(villageSeed, slot), season, around);
}

/** How long after a clap beat `onClap` stays true (a clap's flash), in beats. */
const CLAP_WINDOW = 0.3;

/**
 * Where a tune is `secondsIn` seconds after it began: `bar` counts bars from the first downbeat (−1 in
 * the lead before it), `beat` is the beat within the bar (fractional, 0 ≤ beat < beatsPerBar),
 * `clapping` is the crowd clapping along now, `onClap` is the moment just after one of its claps.
 */
export function beatAt(tune: Tune, secondsIn: number): { beat: number; bar: number; clapping: boolean; onClap: boolean } {
  const beats = ((secondsIn - tune.offset) * tune.bpm) / 60;
  const bar = Math.floor(beats / tune.beatsPerBar);
  const beat = beats - bar * tune.beatsPerBar;
  const clapping = secondsIn >= tune.clapFrom && secondsIn < tune.clapTo;
  const onClap = clapping && tune.claps.some((c) => beat >= c && beat < c + CLAP_WINDOW);
  return { beat, bar, clapping, onClap };
}
